import {
  ChevronDown,
  Languages,
  PlayCircle,
  WandSparkles,
  Mic,
  Speech,
  CircleGauge
} from "lucide-react"
import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"

import { SpeakerIcon } from "./SpeakerIcon"
import { Toast } from "./ui/toast"
import type { LangCode } from "./tts/types"
import { Button } from "./ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "./ui/dropdown-menu"
import { useUser } from "@/lib/context/user-context"

interface FloatingToolbarProps {
  onClose?: () => void
  onAnalyze?: () => void
  onPlaySlower?: (speed: number) => void
  onPlaySpeed?: (speed: number) => void
  onPlay?: () => void
  triggerPosition?: { x: number; y: number }
  selectedText?: string
  speakerLang?: string
  isFromTextSelection?: boolean
  onTranslate?: () => void
  isOneWord?: boolean
  onTriggerShadowing?: () => void // 新增：触发Shadowing Practice的回调
}

export function FloatingToolbar({
  onClose,
  onAnalyze,
  onPlaySlower,
  onPlaySpeed,
  triggerPosition,
  selectedText,
  speakerLang = "en-US",
  isFromTextSelection = false,
  onTranslate,
  isOneWord = false,
  onTriggerShadowing
}: FloatingToolbarProps) {
  const [showToast, setShowToast] = useState(false)
  const [currentPosition, setCurrentPosition] = useState(triggerPosition || { x: 0, y: 0 })
  const { user } = useUser()

  // Handle click outside to close toolbar
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const toolbar = document.querySelector('[role="toolbar"]')
      if (toolbar && !toolbar.contains(e.target as Node)) {
        onClose?.()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [onClose])

  // Handle position tracking for smooth following
  useEffect(() => {
    if (!triggerPosition) return

    // Update initial position
    setCurrentPosition(triggerPosition)

    // Cache target element to avoid repeated DOM queries
    let cachedTargetElement: Element | null = null
    let animationFrameId: number | null = null

    // Optimized position update using requestAnimationFrame
    const updatePosition = () => {
      if (animationFrameId) return // Prevent multiple concurrent updates

      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = null

        // Use cached element if available, otherwise find it
        if (!cachedTargetElement || !document.contains(cachedTargetElement)) {
          // Method 1: Look for active chunk with highlighting
          const activeGroup = document.querySelector('[data-active="true"]')
          if (activeGroup) {
            const chunkSpans = activeGroup.querySelectorAll('span[class*="cursor-pointer"]')
            if (chunkSpans.length > 0) {
              // Try to find the highlighted one
              const highlightedChunk = Array.from(chunkSpans).find(span => 
                span.classList.contains('bg-[#FF7F50]/20') || 
                span.classList.contains('bg-primary') ||
                getComputedStyle(span).backgroundColor !== 'rgba(0, 0, 0, 0)'
              )
              cachedTargetElement = highlightedChunk || chunkSpans[0]
            }
          }

          // Method 2: Fallback to any highlighted element
          if (!cachedTargetElement) {
            cachedTargetElement = document.querySelector('[data-word-highlight]')
          }
        }

        if (cachedTargetElement) {
          const rect = cachedTargetElement.getBoundingClientRect()
          const newX = rect.left
          const newY = rect.bottom + 5

          // Use a smaller threshold for more responsive updates
          setCurrentPosition(prev => {
            if (Math.abs(prev.x - newX) > 1 || Math.abs(prev.y - newY) > 1) {
              return { x: newX, y: newY }
            }
            return prev
          })
        }
      })
    }

    // Throttled scroll handler
    let scrollTimeout: NodeJS.Timeout | null = null
    const handleScroll = () => {
      if (scrollTimeout) return
      
      scrollTimeout = setTimeout(() => {
        updatePosition()
        scrollTimeout = null
      }, 8) // Very short throttle for responsive feel
    }

    // Setup event listeners
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', updatePosition, { passive: true })

    // Setup ResizeObserver with throttling
    let resizeObserver: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      let resizeTimeout: NodeJS.Timeout | null = null
      
      resizeObserver = new ResizeObserver(() => {
        if (resizeTimeout) return
        
        resizeTimeout = setTimeout(() => {
          updatePosition()
          resizeTimeout = null
        }, 8)
      })

      // Observe the active group
      const activeGroup = document.querySelector('[data-active="true"]')
      if (activeGroup) {
        resizeObserver.observe(activeGroup)
      }
    }

    // Cleanup function
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }
      
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', updatePosition)
      
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [triggerPosition])

  const handleAnalyzeClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // 检查用户是否已登录且为付费用户
    const isPremium = user?.user_metadata?.isPremium === true
    console.log("isPremium", isPremium)

    if (user && isPremium) {
      // 用户已登录且为付费用户，执行分析功能
      onAnalyze?.()
    } else {
      // 用户未登录或非付费用户，显示升级提示
      setShowToast(true)
    }
  }

  const handleShadowingClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    console.log("🎤 Shadowing button clicked")
    onTriggerShadowing?.()
  }

  const toolbarContent = (
    <div
      role="toolbar"
      aria-orientation="horizontal"
      dir="ltr"
      className="bg-popover flex select-none items-center z-[9999] overflow-x-auto whitespace-nowrap rounded-xl border p-1 shadow-lg scrollbar-hide print:hidden max-w-[80vw] pointer-events-auto"
      style={{ 
        backgroundColor: 'hsl(var(--popover))',
        position: 'fixed',
        top: currentPosition?.y ? `${currentPosition.y}px` : '70%',
        left: currentPosition?.x ? `${currentPosition.x}px` : '50%',
        transform: currentPosition?.x && currentPosition?.y ? 'none' : 'translate(-50%, -50%)',
        // No transition for instant following
      }}>
      {/* Speak Button Group */}
      <div className="group/toolbar-group relative flex">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-[6px] p-2 bg-popover"
            asChild
          >
            <div>
              <SpeakerIcon
                provider="EdgeTTS"
                text={selectedText}
                lang={speakerLang as LangCode}
                size={16}
              />
            </div>
          </Button>
        </div>
        <div className="mx-1.5 py-0.5 group-last/toolbar-group:!hidden">
          <div className="shrink-0 bg-border h-full w-px" />
        </div>
      </div>

      {/* Translate/Analyze Group */}
      {/* <div className="group/toolbar-group relative flex">
        <div className="flex items-center">
          <Button
            variant="ghost"
            className={`rounded-[6px] inline-flex items-center whitespace-nowrap h-7 px-2 justify-between gap-1 pr-1 ${
              isOneWord ? 'cursor-pointer' : 'cursor-pointer opacity-70 hover:opacity-100'
            }`}
            onClick={isOneWord ? onTranslate : handleAnalyzeClick}>
            <div className="flex flex-1 items-center gap-2 whitespace-nowrap">
              {isOneWord ? (
                <>
                  <Languages className="size-4" />
                  <span>Translate</span>
                </>
              ) : (
                <>
                  <WandSparkles className="size-4" />
                  <span className="">Analyze</span>
                </>
              )}
            </div>
          </Button>
        </div>
        <div className="mx-1.5 py-0.5 group-last/toolbar-group:!hidden">
          <div className="shrink-0 bg-border h-full w-px" />
        </div>
      </div> */}

      {/* Speech/Shadowing Group - 只在非单词模式且非文本选择模式下显示 */}
      {!isOneWord && !isFromTextSelection && onTriggerShadowing && (
        <div className="group/toolbar-group relative flex">
          <div className="flex items-center">
            <Button
              variant="ghost"
              className="rounded-[6px] inline-flex items-center whitespace-nowrap h-7 px-2 justify-between gap-1 pr-1 cursor-pointer"
              onClick={handleShadowingClick}
            >
              <div className="flex flex-1 items-center gap-2 whitespace-nowrap">
                <Speech className="size-4" />
                <span>Shadowing</span>
              </div>
            </Button>
          </div>
          <div className="mx-1.5 py-0.5 group-last/toolbar-group:!hidden">
            <div className="shrink-0 bg-border h-full w-px" />
          </div>
        </div>
      )}

      {/* 只在非文本选择模式下显示其他按钮 */}
      {!isFromTextSelection && (
        <>
          {/* Play Slower Group */}
          <div className="group/toolbar-group relative flex">
            <div className="flex items-center">
              <Button
                variant="ghost"
                onClick={() => {
                  onPlaySlower?.(0.75)
                }}
                className="rounded-[6px] inline-flex items-center whitespace-nowrap h-7 px-2 justify-between gap-1 pr-1 cursor-pointer"
              >
                <div className="flex flex-1 items-center gap-2 whitespace-nowrap">
                  <PlayCircle className="size-4" />
                  <span className="">0.75x</span>
                </div>
              </Button>
            </div>
            <div className="mx-1.5 py-0.5 group-last/toolbar-group:!hidden">
              <div className="shrink-0 bg-border h-full w-px" />
            </div>
          </div>

          {/* Speed Dropdown Group */}
          <div className="group/toolbar-group relative hidden has-[button]:flex">
            <div className="flex items-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    // size="sm"
                    className="rounded-[6px] inline-flex items-center whitespace-nowrap h-7 px-2 justify-between gap-1 pr-1 font-normal">
                    <div className="flex flex-1 items-center gap-2 whitespace-nowrap">
                      {/* Speed */}
                      <CircleGauge className="size-4" />
                    </div>
                    <ChevronDown className="size-3.5 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  style={{ minWidth: "8rem" }}
                  align="start">
                  <DropdownMenuItem
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onPlaySpeed?.(0.5)
                    }}
                    className="cursor-pointer">
                    <PlayCircle className="size-5" />
                    <span className="pl-2">0.5x</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onPlaySpeed?.(1.25)
                    }}
                    className="cursor-pointer">
                    <PlayCircle className="size-5" />
                    <span className="pl-2">1.25x</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="mx-1.5 py-0.5 group-last/toolbar-group:!hidden">
              <div className="shrink-0 bg-border h-full w-px" />
            </div>
          </div>
        </>
      )}
    </div>
  )

  return typeof window !== 'undefined' ? (
    <>
      {createPortal(toolbarContent, document.body)}
      {showToast && (
        <Toast
          message="Upgrade to Premium to unlock the Analyze feature and enhance your learning experience!"
          type="premium"
          onClose={() => setShowToast(false)}
          duration={4000}
        />
      )}
    </>
  ) : null
}
